// ============================================================================
// MODIFIER UN CLIENT — la modale ouverte par le crayon de la fiche et de la liste, et par « Compléter → ».
//
// UTILITÉ : corriger l'identité légale, le siège ou le contact d'un client sans le recréer. Deux temps (maquette du
// 28/10) : le formulaire, puis les seuls champs modifiés, l'ancienne valeur barrée.
// ⚠️ LA RÈGLE DE SAISIE EST CELLE DU SERVEUR (`companyIdentityProblems`, dans le contrat) : l'écran la montre sous les
//    champs, le serveur la refait — jamais deux règles qui finiraient par se contredire.
// ⚠️ CE QUE L'ENREGISTREMENT A FAIT SE DIT : l'identité est renvoyée aux magasins du client, et un renvoi en échec reste
//    affiché avec son motif au lieu d'un succès (§23.0). « Redéclarer au back-office » sur la fiche du magasin le rattrape.
// ============================================================================
import { useEffect, useId, useState, type CSSProperties, type FormEvent, type MouseEvent } from 'react';
import { rovingRadio, showToast, useOverlayPanel } from '@orgazone/ui-web';
import {
  companyIdentityProblems, companyVatNumber, formatSiren, formatSiret, isValidSiren, normalizeNaf,
} from '@orgazone/sync-contract';
import { api, ApiError, type TenantDetail, type TenantIdentity, type TenantStoreResend } from '../api';
import { glassBoxSt, glassOverlaySt } from '../glass-modal';
import { formPanelRef } from '../ui';

/** Les champs saisis au clavier — le régime de TVA, lui, se choisit. */
type Key = Exclude<keyof TenantIdentity, 'vatFranchise' | 'vatNumberAssigned'>;
const KEYS: Key[] = ['name', 'commercialName', 'siren', 'siretHq', 'naf', 'street', 'zip', 'city', 'email'];

const LABEL: Record<Key, string> = {
  name: 'Raison sociale', commercialName: 'Nom commercial', siren: 'SIREN', siretHq: 'SIRET du siège', naf: 'Code NAF / APE',
  street: 'Adresse du siège', zip: 'Code postal', city: 'Ville', email: 'E-mail de contact',
};

/** Ce qui s'imprime sur les tickets : modifier autre chose ne renvoie rien aux caisses. */
const ON_TICKETS: Key[] = ['name', 'siren', 'siretHq', 'naf', 'street', 'zip', 'city'];

const trimOrNull = (v: string | null | undefined): string | null => { const t = (v ?? '').trim(); return t === '' ? null : t; };

/** L'identité enregistrée sous les noms du formulaire — la même lecture que le serveur (`identityOf`). */
export function identityFromTenant(t: TenantDetail['tenant']): TenantIdentity {
  return {
    name: trimOrNull(t.name), commercialName: trimOrNull(t.commercialName), siren: trimOrNull(t.siren), siretHq: trimOrNull(t.siretHq),
    naf: trimOrNull(t.naf), street: trimOrNull(t.address?.voie), zip: trimOrNull(t.address?.cp), city: trimOrNull(t.address?.ville),
    email: trimOrNull(t.contact?.email),
    vatFranchise: t.vatFranchise ?? null,
    // Un numéro enregistré pour une société en franchise est un numéro qui lui a été attribué.
    vatNumberAssigned: t.vatFranchise === true && !!trimOrNull(t.vatIntra),
  };
}

/** Le régime de TVA en mots. */
const regimeText = (v: boolean | null): string => (v === true ? 'franchise en base (art. 293 B du CGI)' : v === false ? 'assujetti à la TVA' : '— non renseigné');

/** Une valeur comparée à l'enregistrée : le NAF sous sa forme officielle, comme au serveur. */
const comparable = (k: Key, v: string | null): string | null => (k === 'naf' && v ? normalizeNaf(v) ?? v : v);

/** Les chiffres seuls, pour un identifiant collé avec ses espaces (« 542 051 180 »). */
const digits = (v: string, max: number) => v.replace(/\D/g, '').slice(0, max);
const CLEAN: Partial<Record<Key, (v: string) => string>> = {
  siren: (v) => digits(v, 9), siretHq: (v) => digits(v, 14), zip: (v) => digits(v, 5), naf: (v) => v.toUpperCase().slice(0, 6),
};

type SireneState = 'idle' | 'load' | 'ok' | 'none' | 'err';

export function ClientEditModal({ tenantId, onClose, onSaved }: {
  tenantId: string;
  onClose: () => void;
  /** Appelé après un enregistrement, pour relire la fiche ou la liste. */
  onSaved: () => void;
}) {
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<Key, string>>(() => Object.fromEntries(KEYS.map((k) => [k, ''])) as Record<Key, string>);
  // Le régime de TVA se choisit, il ne se tape pas : il vit à part des champs texte.
  const [regime, setRegime] = useState<{ franchise: boolean | null; assigned: boolean }>({ franchise: null, assigned: false });
  const [step, setStep] = useState<'form' | 'recap' | 'done'>('form');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [serverProblems, setServerProblems] = useState<Partial<Record<string, string>>>({});
  const [sirene, setSirene] = useState<{ state: SireneState; filled: number }>({ state: 'idle', filled: 0 });
  const [resends, setResends] = useState<TenantStoreResend[]>([]);
  const titleId = useId();
  const uid = useId();
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });

  // La fiche est relue à l'ouverture : c'est sa version ACTUELLE que le formulaire modifie, et que le serveur compare.
  useEffect(() => {
    let live = true;
    api.tenant(tenantId)
      .then((d) => {
        if (!live) return;
        setDetail(d);
        const base = identityFromTenant(d.tenant);
        setDraft(Object.fromEntries(KEYS.map((k) => [k, base[k] ?? ''])) as Record<Key, string>);
        setRegime({ franchise: base.vatFranchise, assigned: base.vatNumberAssigned });
      })
      .catch((e: unknown) => { if (live) setLoadErr((e as Error).message); });
    return () => { live = false; };
  }, [tenantId]);

  useEffect(() => { if (step !== 'form') panelRef.current?.focus(); }, [step, panelRef]);

  const base = detail ? identityFromTenant(detail.tenant) : null;
  const values: TenantIdentity = {
    ...(Object.fromEntries(KEYS.map((k) => [k, trimOrNull(draft[k])])) as Omit<TenantIdentity, 'vatFranchise' | 'vatNumberAssigned'>),
    vatFranchise: regime.franchise,
    vatNumberAssigned: regime.franchise === true && regime.assigned,
  };
  const problems: Partial<Record<string, string>> = base
    ? {
      ...companyIdentityProblems(
        { name: draft.name, siren: draft.siren, siretHq: draft.siretHq, naf: draft.naf, zip: draft.zip, email: draft.email },
        { siren: base.siren, siretHq: base.siretHq },
      ),
      ...serverProblems,
    }
    : {};
  const changed = base ? KEYS.filter((k) => comparable(k, values[k]) !== comparable(k, base[k])) : [];
  const regimeChanged = !!base && (values.vatFranchise !== base.vatFranchise || values.vatNumberAssigned !== base.vatNumberAssigned);
  const anyChange = changed.length > 0 || regimeChanged;
  const nProblems = Object.keys(problems).length;
  const ready = !!base && nProblems === 0 && anyChange;
  const sirenValid = isValidSiren(draft.siren);
  const sirenChanged = !!base?.siren && sirenValid && draft.siren !== base.siren;
  // Le numéro que porterait la société : calculé du SIREN, absent en franchise sauf s'il lui a été attribué.
  const vat = companyVatNumber(draft.siren, { franchise: regime.franchise === true, numberAssigned: regime.assigned });
  const vatWas = detail?.tenant.vatIntra ?? null;
  const vatChanged = !!base && (changed.includes('siren') || regimeChanged) && vat !== vatWas;
  const touchesTickets = changed.some((k) => ON_TICKETS.includes(k)) || regimeChanged;

  const set = (k: Key, raw: string) => {
    const v = CLEAN[k] ? CLEAN[k]!(raw) : raw;
    setDraft((d) => ({ ...d, [k]: v }));
    // Un problème rendu par le serveur concerne la valeur qu'il a lue : retaper le champ l'efface.
    setServerProblems((p) => { const { [k]: _gone, ...rest } = p; return rest; });
    if (k === 'siren') setSirene({ state: 'idle', filled: 0 });
  };

  /** Complète depuis l'annuaire des entreprises — le siège du SIREN saisi. Un échec ne bloque rien. */
  const fillFromSirene = async () => {
    if (!sirenValid || sirene.state === 'load') return;
    setSirene({ state: 'load', filled: 0 });
    try {
      const r = await api.sireneSearch(draft.siren);
      const hit = r.results.find((x) => x.siren === draft.siren);
      if (!hit) { setSirene({ state: 'none', filled: 0 }); return; }
      const got: Partial<Record<Key, string>> = {
        name: hit.name, siretHq: hit.siret ?? '', naf: hit.naf ? normalizeNaf(hit.naf) ?? hit.naf : '',
        street: hit.street ?? '', zip: hit.cp ?? '', city: hit.city ?? '',
      };
      const keep = Object.fromEntries(Object.entries(got).filter(([, v]) => !!v && v.trim() !== '')) as Partial<Record<Key, string>>;
      setDraft((d) => ({ ...d, ...keep }));
      setSirene({ state: 'ok', filled: Object.keys(keep).length });
    } catch {
      setSirene({ state: 'err', filled: 0 });
    }
  };

  const submit = (e: FormEvent) => { e.preventDefault(); if (ready) { setErr(null); setStep('recap'); } };
  const back = () => { setErr(null); setStep('form'); };
  const save = async () => {
    if (!ready || !base) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.updateTenant(tenantId, { values, base });
      onSaved();
      const failed = r.stores.filter((s) => s.outcome === 'failed');
      if (failed.length === 0) {
        const sent = r.stores.filter((s) => s.outcome === 'sent').length;
        showToast(sent > 0 ? `Client modifié — identité renvoyée à ${sent} magasin${sent > 1 ? 's' : ''}.` : 'Client modifié.');
        onClose();
        return;
      }
      setResends(r.stores);
      setStep('done');
    } catch (e) {
      // Un refus par champ revient sous ses champs ; le formulaire se rouvre dessus.
      if (e instanceof ApiError && e.errors.length > 0) {
        setServerProblems(Object.fromEntries(e.errors.map((x) => [x.field, x.code])));
        setStep('form');
      }
      setErr((e as Error).message);
    } finally { setBusy(false); }
  };

  const fmt = (k: Key, v: string | null): string => (!v ? '— non renseigné' : k === 'siren' ? formatSiren(v) : k === 'siretHq' ? formatSiret(v) : k === 'naf' ? normalizeNaf(v) ?? v : v);
  const MONO: Key[] = ['siren', 'siretHq', 'naf', 'zip', 'email'];
  const labelSt: CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 };
  const eyebrowSt: CSSProperties = { fontSize: 'var(--t-eyebrow)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink500)', marginBottom: 10 };
  const ctaOff: CSSProperties = { background: 'var(--ink200)', color: 'var(--ink400)', boxShadow: 'none', cursor: 'not-allowed' };

  const field = (k: Key, opts: { wide?: boolean; req?: boolean; ph?: string; hint?: string } = {}) => {
    const id = `${uid}-${k}`;
    const problem = problems[k];
    const note = problem ?? opts.hint;
    return (
      <div key={k} style={{ minWidth: 0, ...(opts.wide ? { gridColumn: '1 / -1' } : null) }}>
        <label htmlFor={id} style={labelSt}>
          {LABEL[k]}
          {opts.req && <span style={{ fontSize: 'var(--t-micro)', fontWeight: 600, color: 'var(--teal-500)' }}>requis</span>}
        </label>
        <input id={id} className={MONO.includes(k) ? 'field-input mono' : 'field-input'} value={draft[k]} placeholder={opts.ph}
          onChange={(e) => set(k, e.target.value)} aria-invalid={problem ? true : undefined} aria-describedby={note ? `${id}-note` : undefined}
          style={{ minHeight: 36, height: 36, background: 'var(--elev)', ...(problem ? { borderColor: 'color-mix(in srgb, var(--da-fg) 55%, transparent)' } : null) }} />
        {note && (
          <span id={`${id}-note`} style={{ display: 'block', fontSize: 'var(--t-meta)', color: problem ? 'var(--da-fg)' : 'var(--ink500)', marginTop: 5, lineHeight: 1.45 }}>{note}</span>
        )}
      </div>
    );
  };

  const sireneNote = {
    load: 'Recherche dans l’annuaire des entreprises…',
    ok: `${sirene.filled} champ${sirene.filled > 1 ? 's' : ''} repris de Sirene — vérifiez avant d’enregistrer.`,
    none: 'Aucune société avec ce SIREN dans l’annuaire : la saisie manuelle reste possible.',
    err: 'Annuaire injoignable : la saisie manuelle reste possible.',
    idle: sirenValid ? 'Le SIREN est valide : complétez les autres champs depuis l’annuaire des entreprises.' : 'Saisissez un SIREN valide pour compléter la fiche depuis l’annuaire des entreprises.',
  }[sirene.state];
  const sireneTone = sirene.state === 'ok' ? 'var(--ok-fg)' : sirene.state === 'none' || sirene.state === 'err' ? 'var(--wa-fg)' : 'var(--ink500)';

  const recapRow = (k: string, v: string, was: string, mono: boolean) => (
    <div key={k} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid var(--ink100)' }}>
      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', flex: '0 0 150px' }}>{k}</span>
      <span className={mono ? 'mono' : undefined} style={{ fontSize: mono ? 'var(--t-dense)' : 'var(--t-body)', fontWeight: 600, color: v.startsWith('—') ? 'var(--ink400)' : 'var(--ink900)', minWidth: 0, overflowWrap: 'anywhere' }}>{v}</span>
      <s className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)' }}>{was}</s>
    </div>
  );

  const foot = step === 'done' ? ''
    : nProblems > 0 ? `${nProblems} champ${nProblems > 1 ? 's' : ''} à corriger`
      : !anyChange ? 'Aucune modification.'
        : (() => { const n = changed.length + (regimeChanged ? 1 : 0); return `${n} modification${n > 1 ? 's' : ''} · journalisée${n > 1 ? 's' : ''}`; })();

  const name = detail?.tenant.name ?? '';
  const content = (
    <>
      <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'flex-start', gap: 14, flexShrink: 0 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink500)' }}>Client · {name}</div>
          <h3 id={titleId} style={{ margin: '5px 0 0', fontSize: 16, fontWeight: 700, letterSpacing: '-.02em' }}>
            {step === 'form' ? 'Modifier le client' : step === 'recap' ? 'Confirmer les modifications' : 'Client modifié'}
          </h3>
          <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5, textWrap: 'pretty' }}>
            {step === 'form'
              ? 'Identité légale, siège et contact. Le type de commerce se règle magasin par magasin, et les licences dans l’onglet Licences.'
              : step === 'recap'
                ? 'Relisez avant d’enregistrer : l’ancienne valeur est barrée.'
                : 'Les modifications sont enregistrées, mais l’identité n’a pas atteint tous les magasins.'}
          </div>
        </div>
        <button type="button" className="btn-icon" onClick={onClose} disabled={busy} title="Fermer" aria-label="Fermer">✕</button>
      </div>

      <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {loadErr && <div className="err" role="alert">La fiche du client n’a pas pu être lue : {loadErr}</div>}
        {!loadErr && !detail && <p className="note" role="status">Lecture de la fiche…</p>}

        {detail && step === 'form' && <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '11px 14px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--panel)' }}>
            <div role="status" style={{ flex: '1 1 260px', minWidth: 0, fontSize: 'var(--t-note)', lineHeight: 1.5, color: sireneTone }}>{sireneNote}</div>
            <button type="button" className="btn-ghost sm" onClick={() => void fillFromSirene()} disabled={!sirenValid || sirene.state === 'load'}>
              {sirene.state === 'load' ? 'Recherche…' : 'Compléter depuis Sirene'}
            </button>
          </div>
          <div>
            <div className="mono" style={eyebrowSt}>Identité légale</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
              {field('name', { req: true, ph: 'Maison Vireau SAS' })}
              {field('commercialName', { ph: 'Vireau' })}
              {field('siren', { ph: '9 chiffres', hint: 'La clé de contrôle est vérifiée à la saisie.' })}
              {field('siretHq', { ph: '14 chiffres', hint: 'Il commence par le SIREN.' })}
              {field('naf', { ph: '47.71Z', hint: 'Nomenclature officielle INSEE.' })}
              {/* Le RÉGIME DE TVA (décision du 28/10) : il décide si les caisses facturent la TVA, et si la société a un
                  numéro. Choisi, jamais tapé. */}
              <div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
                <span id={`${uid}-vat`} style={labelSt}>Régime de TVA</span>
                <div role="radiogroup" aria-labelledby={`${uid}-vat`} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {([[false, 'Assujetti à la TVA'], [true, 'Franchise en base (art. 293 B du CGI)']] as const).map(([k, l], i) => (
                    <button key={l} type="button" role="radio" aria-checked={regime.franchise === k} className={regime.franchise === k ? 'seg active' : 'seg'}
                      onClick={() => setRegime((r) => ({ ...r, franchise: k }))}
                      {...rovingRadio({ index: i, count: 2, checked: regime.franchise === k, anyChecked: regime.franchise !== null, onPick: (n) => setRegime((r) => ({ ...r, franchise: n === 1 })) })}>
                      {l}
                    </button>
                  ))}
                </div>
                {regime.franchise === null && (
                  <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 5, lineHeight: 1.45 }}>
                    Pas encore réglé à la Console : il a pu l’être au back-office, magasin par magasin. Le choisir ici le fait descendre à tous les magasins.
                  </span>
                )}
                {problems.vatFranchise && <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--da-fg)', marginTop: 5 }}>{problems.vatFranchise}</span>}
                {regime.franchise === true && (
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, fontSize: 'var(--t-dense)', color: 'var(--ink700)', lineHeight: 1.45, cursor: 'pointer' }}>
                    <input type="checkbox" checked={regime.assigned} onChange={(e) => setRegime((r) => ({ ...r, assigned: e.target.checked }))} style={{ marginTop: 2 }} />
                    <span>Un numéro de TVA intracommunautaire lui a été attribué — achats ou ventes dans l’Union au-delà des seuils, ou option.</span>
                  </label>
                )}
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 12px', padding: '10px 12px', borderRadius: 'var(--r-sm)', border: '1px dashed var(--ink200)', background: 'var(--ink-50)' }}>
                <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink600)' }}>N° TVA intracommunautaire</span>
                <span className="mono" style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: vat ? 'var(--ink900)' : 'var(--ink400)' }}>
                  {vat ?? (regime.franchise === true && !regime.assigned ? 'aucun — franchise en base' : '— renseignez le SIREN')}
                </span>
                <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>calculé depuis le SIREN et le régime, jamais saisi</span>
              </div>
            </div>
          </div>
          <div>
            <div className="mono" style={eyebrowSt}>Siège et contact</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
              {field('street', { wide: true, ph: '3 place Kléber' })}
              {field('zip', { ph: '67000' })}
              {field('city', { ph: 'Strasbourg' })}
              {field('email', { wide: true, ph: 'administratif@enseigne.fr', hint: 'Destinataire des courriers administratifs d’Orgazone. Ce n’est pas un compte de connexion.' })}
            </div>
          </div>
          {sirenChanged && (
            <div role="alert" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 14px', border: '1px solid color-mix(in srgb, var(--wa-fg) 35%, transparent)', borderRadius: 'var(--r-sm)', background: 'var(--wa-bg)' }}>
              <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 5, background: 'color-mix(in srgb, var(--wa-fg) 22%, transparent)', color: 'var(--wa-fg)', display: 'grid', placeItems: 'center', fontSize: 'var(--t-meta)', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>!</span>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--wa-fg)', lineHeight: 1.55 }}>
                Changer de SIREN, c’est changer d’entité légale. Corrigez une erreur de saisie ici ; pour une cession du fonds, créez plutôt un nouveau client.
              </div>
            </div>
          )}
        </>}

        {detail && base && step === 'recap' && <>
          <div style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', background: 'var(--panel)' }}>
            {changed.map((k) => recapRow(LABEL[k], fmt(k, values[k]), fmt(k, base[k]), MONO.includes(k)))}
            {regimeChanged && values.vatFranchise !== base.vatFranchise && recapRow('Régime de TVA', regimeText(values.vatFranchise), regimeText(base.vatFranchise), false)}
            {vatChanged && recapRow('N° TVA (calculé)', vat ?? 'aucun', vatWas ?? '— non renseigné', true)}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 14px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--in-bg)' }}>
            <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 5, background: 'color-mix(in srgb, var(--in-fg) 22%, transparent)', color: 'var(--in-fg)', display: 'grid', placeItems: 'center', fontSize: 'var(--t-meta)', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>i</span>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--in-fg)', lineHeight: 1.55 }}>
              {regimeChanged && values.vatFranchise !== base.vatFranchise && (
                <span style={{ display: 'block', marginBottom: 6 }}>
                  {values.vatFranchise
                    ? 'En franchise en base, les caisses factureront sans TVA dès leur prochain contact et imprimeront « TVA non applicable, art. 293 B du CGI » sur chaque pièce.'
                    : 'Assujetties, les caisses factureront de nouveau la TVA dès leur prochain contact.'}
                </span>
              )}
              {touchesTickets
                ? 'Les mentions légales imprimées par les caisses suivent à leur prochain contact — le SIRET et l’adresse du siège seulement pour les magasins qui n’ont pas les leurs. Les tickets déjà émis ne changent pas : ils restent signés avec l’identité du jour de leur émission.'
                : 'Rien de ce que vous modifiez ne s’imprime sur les tickets : les caisses ne reçoivent rien.'}
            </div>
          </div>
        </>}

        {step === 'done' && (
          <div style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', background: 'var(--panel)' }}>
            {resends.map((s) => (
              <div key={s.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid var(--ink100)' }}>
                <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, flex: '0 0 170px' }}>{s.name}</span>
                <span style={{ fontSize: 'var(--t-note)', color: s.outcome === 'failed' ? 'var(--da-fg)' : 'var(--ink600)', minWidth: 0, flex: 1 }}>
                  {s.outcome === 'sent' ? 'identité renvoyée' : s.outcome === 'skipped' ? `rien à renvoyer — ${s.detail}` : `non reçue — ${s.detail}`}
                </span>
              </div>
            ))}
            <div style={{ padding: '10px 14px', fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5 }}>
              « Redéclarer au back-office », sur la fiche d’un magasin, renvoie son identité une fois le back-office joignable.
            </div>
          </div>
        )}
        {err && <div className="err" role="alert">{err}</div>}
      </div>

      <div style={{ padding: '14px 22px', borderTop: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: 'var(--ink-50)', flexShrink: 0 }}>
        {step !== 'done' && <button type="button" className="btn-ghost" onClick={step === 'recap' ? back : onClose} disabled={busy}>{step === 'recap' ? '← Retour' : 'Annuler'}</button>}
        <div style={{ flex: '1 1 150px', minWidth: 0, fontSize: 'var(--t-meta)', lineHeight: 1.45, color: nProblems > 0 && step === 'form' ? 'var(--da-fg)' : 'var(--ink500)' }}>{foot}</div>
        {step === 'form' && <button type="submit" className="btn-cta" disabled={!ready} style={ready ? undefined : ctaOff}>Vérifier les modifications →</button>}
        {step === 'recap' && (
          <button type="button" className="btn-cta" onClick={() => void save()} disabled={busy} style={busy ? ctaOff : undefined}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        )}
        {step === 'done' && <button type="button" className="btn-cta" onClick={onClose}>Fermer</button>}
      </div>
    </>
  );
  const box: CSSProperties = { ...glassBoxSt(660), outline: 'none' };
  const stop = (e: MouseEvent) => e.stopPropagation();
  return (
    <div style={glassOverlaySt} onClick={busy ? undefined : onClose}>
      {step === 'form'
        ? <form style={box} ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={stop} onSubmit={submit}>{content}</form>
        : <div style={box} ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onClick={stop}>{content}</div>}
    </div>
  );
}
